import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Zap, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://devisfast-backend.onrender.com/api';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0,00 €';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const PublicQuoteViewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refusing, setRefusing] = useState(false);
  const [refused, setRefused] = useState(false);
  const [confirmRefuse, setConfirmRefuse] = useState(false);

  useEffect(() => { fetchQuote(); }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API_BASE}/view/${token}`);
      setData(response.data);
      if (response.data.already_refused) setRefused(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleRefuse = async () => {
    setRefusing(true);
    try {
      await axios.post(`${API_BASE}/refuse/${token}`);
      setRefused(true);
      setConfirmRefuse(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setRefusing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-white">
          <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
          <p className="text-gray-400">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#1a1a24] rounded-2xl p-8 text-center border border-white/10">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Lien invalide</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const { quote, client, company, already_signed } = data;

  if (already_signed) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#1a1a24] rounded-2xl p-8 text-center border border-white/10">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Devis déjà signé</h2>
          <p className="text-gray-400">Ce devis a déjà été accepté et signé.</p>
        </div>
      </div>
    );
  }

  if (refused) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#1a1a24] rounded-2xl p-8 text-center border border-white/10">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Devis refusé</h2>
          <p className="text-gray-400 mb-4">Vous avez refusé ce devis. L'entreprise a été notifiée.</p>
          <p className="text-gray-500 text-sm">N'hésitez pas à les recontacter si vous souhaitez en discuter.</p>
        </div>
      </div>
    );
  }

  const tvaByRate = {};
  quote.lines?.forEach(line => {
    const rate = line.tva_rate;
    if (!tvaByRate[rate]) tvaByRate[rate] = 0;
    tvaByRate[rate] += (line.total_ht || 0) * (rate / 100);
  });
  const tvaRates = Object.entries(tvaByRate).sort(([a], [b]) => parseFloat(a) - parseFloat(b));
  const multipleTvaRates = tvaRates.length > 1;
  const hasDiscount = !!(quote.discount_value && quote.discount_value > 0);
  const subtotalHT = quote.lines?.reduce((sum, l) => sum + (l.total_ht || 0), 0) || 0;
  const discountAmount = hasDiscount
    ? quote.discount_type === 'percent' ? subtotalHT * quote.discount_value / 100 : quote.discount_value
    : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <header className="bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/5 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-sm">DEVISFAST</span>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <p className="text-gray-400 text-sm mb-1">{company?.name}</p>
          <h1 className="text-2xl font-bold mb-1">Votre devis {quote.quote_number}</h1>
          <p className="text-gray-400 text-sm">Valide jusqu'au {formatDate(quote.validity_date)}</p>
        </div>

        <div className="bg-[#1a1a24] rounded-2xl p-5 border border-white/5 mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Destinataire</p>
          <p className="font-semibold">{client?.name}</p>
          {client?.company && <p className="text-gray-400 text-sm">{client.company}</p>}
          <p className="text-gray-400 text-sm">{client?.address}, {client?.postal_code} {client?.city}</p>
        </div>

        <div className="bg-[#1a1a24] rounded-2xl border border-white/5 mb-4 overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Détail des prestations</p>
          </div>
          <div className="divide-y divide-white/5">
            {quote.lines?.map((line, i) => (
              <div key={i} className="p-5 flex justify-between items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium text-sm">{line.description}</p>
                  <p className="text-gray-500 text-xs mt-1">
                    {line.quantity} × {formatCurrency(line.unit_price_ht)} · TVA {line.tva_rate}%
                  </p>
                </div>
                <p className="font-semibold text-sm flex-shrink-0">{formatCurrency(line.total_ht)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#1a1a24] rounded-2xl p-5 border border-white/5 mb-4">
          {hasDiscount && (
            <>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Sous-total HT</span>
                <span>{formatCurrency(subtotalHT)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-red-400">Remise {quote.discount_type === 'percent' ? `(${quote.discount_value}%)` : ''}</span>
                <span className="text-red-400">-{formatCurrency(discountAmount)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">Total HT</span>
            <span>{formatCurrency(quote.total_ht)}</span>
          </div>
          {!hasDiscount && tvaRates.map(([rate, amount]) => (
            <div key={rate} className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">TVA {rate}%</span>
              <span>{formatCurrency(amount)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">{!hasDiscount && multipleTvaRates ? 'Total TVA' : 'TVA'}</span>
            <span>{formatCurrency(quote.total_tva)}</span>
          </div>
          <div className="flex justify-between font-bold text-lg pt-3 border-t border-white/10">
            <span>Total TTC</span>
            <span className="text-[#2563EB]">{formatCurrency(quote.total_ttc)}</span>
          </div>
        </div>

        {quote.notes && (
          <div className="bg-[#1a1a24] rounded-2xl p-5 border border-white/5 mb-6">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</p>
            <p className="text-gray-300 text-sm">{quote.notes}</p>
          </div>
        )}

        {!confirmRefuse ? (
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/sign/${token}`)}
              className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors text-lg"
            >
              <Check className="w-5 h-5" />
              Accepter et signer le devis
            </button>
            <button
              onClick={() => setConfirmRefuse(true)}
              className="w-full bg-transparent border border-white/10 hover:border-red-500/40 text-gray-400 hover:text-red-400 font-medium py-3 rounded-2xl flex items-center justify-center gap-2 transition-colors"
            >
              <X className="w-4 h-4" />
              Refuser ce devis
            </button>
            <p className="text-center text-gray-500 text-xs mt-2">
              Vous pouvez fermer cette page et revenir plus tard.
            </p>
          </div>
        ) : (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
            <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-2">Confirmer le refus ?</h3>
            <p className="text-gray-400 text-sm mb-5">L'entreprise sera notifiée que vous refusez ce devis.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRefuse(false)}
                className="flex-1 border border-white/10 text-gray-400 py-3 rounded-xl hover:bg-white/5 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRefuse}
                disabled={refusing}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {refusing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Confirmer le refus
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-gray-600 text-xs mt-8">
          Devis transmis via DevisFast · devisfast.fr
        </p>
      </div>
    </div>
  );
};

export default PublicQuoteViewPage;
